import React from "react";

type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    to: string;
    /** If true, opens in a new tab (sets target and rel defaults). */
    newTab?: boolean;
};

// A link inside an open drawer or dialog (a mobile menu) cannot scroll the
// page while it is open: the overlay locks the page (iOS pins the body with
// position: fixed), and on closing it restores the old scroll position and
// returns focus to its trigger, which cancels a scroll already under way. So
// a link to a section of this page closes the overlay first and scrolls once
// it is gone. Every other click is left to the browser.
const OVERLAY_SELECTOR = '[data-vaul-drawer], [role="dialog"]';
const OVERLAY_WAIT_MS = 1000;
const HEADER_OFFSET = 80;

// The element a link points at on the current page, or null when it names
// another page or no section.
const inPageTarget = (href: string): HTMLElement | null => {
    if (!href.includes("#")) return null;
    let url: URL;
    try {
        url = new URL(href, window.location.href);
    } catch {
        return null;
    }
    if (
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname ||
        url.hash.length < 2
    ) {
        return null;
    }
    return document.getElementById(decodeURIComponent(url.hash.slice(1)));
};

const scrollAfterOverlay = (overlay: Element, target: HTMLElement) => {
    const started = Date.now();
    const waitForClose = () => {
        if (overlay.isConnected && Date.now() - started < OVERLAY_WAIT_MS) {
            window.requestAnimationFrame(waitForClose);
            return;
        }
        // One more frame: the overlay restores the page's scroll in a frame
        // of its own after it goes.
        window.requestAnimationFrame(() => {
            const top =
                target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
            window.scrollTo({ top, behavior: "smooth" });
        });
    };
    window.requestAnimationFrame(waitForClose);
};

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
    ({ to, newTab = false, target, rel, onClick, children, ...rest }, ref) => {
        const finalTarget = newTab ? target ?? "_blank" : target;
        const finalRel = newTab ? rel ?? "noopener noreferrer" : rel;
        const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
            onClick?.(e);
            if (e.defaultPrevented || !to) return;
            const overlay = e.currentTarget.closest(OVERLAY_SELECTOR);
            if (!overlay) return;
            const section = inPageTarget(to);
            if (!section) return;
            e.preventDefault();
            scrollAfterOverlay(overlay, section);
        };
        return (
            <a
                href={to}
                target={finalTarget}
                rel={finalRel}
                ref={ref}
                onClick={handleClick}
                {...rest}
            >
                {children}
            </a>
        );
    }
);
